import { useEffect, useRef, useState } from '@wordpress/element';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

type MarkerColor = 'blue' | 'pink' | 'purple' | 'orange';

type LocationData = {
  markerColour: MarkerColor | string;
  name: string;
  location?: string;
  position: { lat: number; lng: number };
  latitude?: number;
  longitude?: number;
  description?: string;
  employmentType?: string;
};

type Props = {
  section: HTMLElement; // container that has the legend elements
  mapContainer?: HTMLElement; // (unused here, kept for API parity)
  apiKey: string;
  locations: LocationData[];
  showCountryNames?: boolean;
};

const MAP_HEIGHT = 720;

const loadGoogleMaps = (apiKey: string): Promise<typeof google> => {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.google?.maps) return resolve(w.google as typeof google);

    const cbName = '__gmapsInit__' + Math.random().toString(36).slice(2);
    (w as any)[cbName] = () => {
      delete (w as any)[cbName];
      resolve(w.google as typeof google);
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
};

// Maximum zoom level when clicking on a cluster (prevents zooming in too far)
const CLUSTER_MAX_ZOOM = 7;

// Zoom level at which country names become visible (when enabled)
const COUNTRY_LABELS_MIN_ZOOM = 4;

const buildMapStyles = (showLabels: boolean): google.maps.MapTypeStyle[] => [
  { elementType: 'geometry', stylers: [{ visibility: 'simplified' }] },
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  // Country borders — always visible
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }, { color: '#A0B8C4' }, { weight: 0.5 }] },
  // Country labels — shown at higher zoom levels when enabled
  { featureType: 'administrative.country', elementType: 'labels', stylers: [{ visibility: showLabels ? 'on' : 'off' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#6B8A99' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.stroke', stylers: [{ color: '#FAFBFD' }, { weight: 2 }] },
  { featureType: 'landscape', stylers: [{ color: '#CCE0E7' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', stylers: [{ color: '#FAFBFD' }] },
];

export default function InteractiveMap({
  section,
  apiKey,
  locations,
  showCountryNames = false,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  // Legend state
  const [hoverOnBlue, setHoverOnBlue] = useState<boolean>(false);
  const [disableBlue, setDisableBlue] = useState<boolean>(false);
  const [hoverOnPink, setHoverOnPink] = useState<boolean>(false);
  const [disablePink, setDisablePink] = useState<boolean>(false);
  const [hoverOnOrange, setHoverOnOrange] = useState<boolean>(false);
  const [disableOrange, setDisableOrange] = useState<boolean>(false);

  // Keep references for map/clusterer and marker groups
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapsRef = useRef<typeof google.maps | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersByColorRef = useRef<{
    blue: google.maps.Marker[];
    pink: google.maps.Marker[];
    purple: google.maps.Marker[];
    orange: google.maps.Marker[];
    other: google.maps.Marker[];
  }>({ blue: [], pink: [], purple: [], orange: [], other: [] });

  // InfoWindow select (kept from your original)
  const [selected, setSelected] = useState<LocationData | null>(null);

  // Helpers — icons and UI state application
  const makeIcon = (
    maps: typeof google.maps,
    color: MarkerColor,
    highlighted = false
  ) => {
    const colorMap: Record<MarkerColor, string> = {
      blue: '#007BFF',
      pink: '#FF1493',
      purple: '#800080',
      orange: '#FF8C00',
    };
    const fillColor = colorMap[color] || '#007BFF';
    const base = highlighted
      ? { r1: 12, r2: 21, r3: 30, size: 72 }
      : { r1: 10, r2: 17.5, r3: 25, size: 60 };
    const svg = `
      <svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'>
        <circle cx='40' cy='40' r='${base.r1}' fill='${fillColor}' fill-opacity='1'/>
        <circle cx='40' cy='40' r='${base.r2}' fill='${fillColor}' fill-opacity='0.5'/>
        <circle cx='40' cy='40' r='${base.r3}' fill='${fillColor}' fill-opacity='0.2'/>
      </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new maps.Size(base.size, base.size),
      anchor: new maps.Point(base.size / 2, base.size / 2),
    };
  };

  const applyHoverState = () => {
    const maps = mapsRef.current;
    if (!maps) return;

    const { blue, pink, orange } = markersByColorRef.current;

    // Only consider currently visible markers (if disabled they aren't on the map)
    const visible = (m: google.maps.Marker) => !!m.getMap();

    const reset = (
      ms: google.maps.Marker[],
      color: MarkerColor
    ) => {
      ms.filter(visible).forEach((m) => {
        m.setIcon(makeIcon(maps, color, false));
        m.setOpacity(1);
        m.setZIndex(undefined as any);
      });
    };
    const fade = (ms: google.maps.Marker[]) => {
      ms.filter(visible).forEach((m) => {
        m.setOpacity(0.25);
        m.setZIndex(1);
      });
    };
    const strong = (ms: google.maps.Marker[], color: MarkerColor) => {
      ms.filter(visible).forEach((m) => {
        m.setOpacity(1);
        m.setIcon(makeIcon(maps, color, true));
        m.setZIndex(1000);
      });
    };

    // reset all colour sets
    reset(blue, 'blue');
    reset(pink, 'pink');
    reset(orange, 'orange');

    // apply hover logic (only if exactly one colour is hovered)
    const hoveredColors = [hoverOnBlue && 'blue', hoverOnPink && 'pink', hoverOnOrange && 'orange'].filter(Boolean);
    if (hoveredColors.length === 1) {
      const hovered = hoveredColors[0] as MarkerColor;
      const colorGroups: { color: MarkerColor; markers: google.maps.Marker[] }[] = [
        { color: 'blue', markers: blue },
        { color: 'pink', markers: pink },
        { color: 'orange', markers: orange },
      ];
      colorGroups.forEach(({ color, markers }) => {
        if (color === hovered) strong(markers, color);
        else fade(markers);
      });
    }
    // if multiple/none hovered, we keep defaults
  };

  const setColorDisabled = (color: 'blue' | 'pink' | 'orange', disabled: boolean) => {
    const cl = clustererRef.current;
    const map = mapRef.current;
    if (!cl || !map) return;

    const set = markersByColorRef.current[color];

    if (disabled) {
      // remove from clusterer and from the map
      cl.removeMarkers(set);
      set.forEach((m) => m.setMap(null));
    } else {
      // add back to map and clusterer
      set.forEach((m) => m.setMap(map));
      cl.addMarkers(set);
    }

    // v2 API -> trigger recompute
    if (typeof (cl as any).render === 'function') {
      (cl as any).render();
    }
  };

  useEffect(() => {
    let map: google.maps.Map | null = null;
    let infoWindow: google.maps.InfoWindow | null = null;
    let markers: google.maps.Marker[] = [];
    let clusterer: MarkerClusterer | null = null;
    let cancelled = false;

    loadGoogleMaps(apiKey).then((g) => {
      if (cancelled || !containerRef.current) return;

      const { maps } = g;
      mapsRef.current = maps;

      const clusterSVG = (fillColor: string) =>
        `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 80 80'>
            <circle cx='40' cy='40' r='12.5' fill='${fillColor}' fill-opacity='1'/>
            <circle cx='40' cy='40' r='20'   fill='${fillColor}' fill-opacity='0.5'/>
            <circle cx='40' cy='40' r='27.5' fill='${fillColor}' fill-opacity='0.2'/>
          </svg>`
        )}`;

      const initialZoom = 2.5;

      map = new maps.Map(containerRef.current!, {
        center: { lat: 20, lng: 0 },
        zoom: initialZoom,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        restriction: {
          latLngBounds: { north: 85, south: -60, west: -180, east: 180 },
          strictBounds: true,
        },
        styles: buildMapStyles(showCountryNames && initialZoom >= COUNTRY_LABELS_MIN_ZOOM),
      });

      mapRef.current = map;

      // Dynamically toggle country labels based on zoom level
      if (showCountryNames) {
        let labelsVisible = initialZoom >= COUNTRY_LABELS_MIN_ZOOM;
        map.addListener('zoom_changed', () => {
          const zoom = map!.getZoom() || 0;
          const shouldShow = zoom >= COUNTRY_LABELS_MIN_ZOOM;
          if (shouldShow !== labelsVisible) {
            labelsVisible = shouldShow;
            map!.setOptions({ styles: buildMapStyles(shouldShow) });
          }
        });
      }

      infoWindow = new maps.InfoWindow();

      // reset buckets each mount
      markersByColorRef.current = { blue: [], pink: [], purple: [], orange: [], other: [] };

      // Markers
      markers = locations.map((loc) => {
        const raw = String(loc.markerColour || '').toLowerCase();
        const color: MarkerColor | 'other' =
          raw === 'pink'
            ? 'pink'
            : raw === 'purple'
            ? 'purple'
            : raw === 'orange'
            ? 'orange'
            : raw === 'blue'
            ? 'blue'
            : 'other';

        const iconColor: MarkerColor =
          color === 'other' ? 'blue' : color;

        const m = new maps.Marker({
          position: loc.position,
          icon: makeIcon(maps, iconColor, false),
        });
        (m as any).color = color;

        if (color in markersByColorRef.current) {
          markersByColorRef.current[color].push(m);
        } else {
          markersByColorRef.current.other.push(m);
        }

        m.addListener('click', () => {
          // preserve your InfoWindow content
          const el = document.createElement('div');
          el.className = 'content';
          el.style.maxWidth = '240px';
          el.innerHTML = `
            <div class="content__location">
              <div style="width:12px;height:12px;border-radius:50%;background:${
                loc.markerColour === 'pink' ? '#FF1493' : loc.markerColour === 'orange' ? '#FF8C00' : loc.markerColour === 'purple' ? '#800080' : '#007BFF'
              }"></div>
              <span>${loc.name ?? ''}</span>
            </div>
            ${
              loc.location ? `<p class="content__name">${loc.location}</p>` : ''
            }
            ${
              loc.employmentType
                ? `<span class="content__type">Employment type: <strong>${loc.employmentType}</strong></span>`
                : ''
            }
            ${
              loc.description
                ? `<span class="content__description">${loc.description}</span>`
                : ''
            }
          `;
          infoWindow!.setContent(el);
          infoWindow!.setPosition(loc.position);
          infoWindow!.open({ map: map! });
          setSelected(loc);
        });

        return m;
      });

      // Clusterer with typed renderer
      clusterer = new MarkerClusterer({
        map: map!,
        markers,
        onClusterClick: (_event: any, cluster: any, clusterMap: google.maps.Map) => {
          const currentZoom = clusterMap.getZoom() || 2;
          const nextZoom = Math.min(currentZoom + 2, CLUSTER_MAX_ZOOM);
          clusterMap.setZoom(nextZoom);
          clusterMap.panTo(cluster.position);
        },
        renderer: {
          render({
            count,
            position,
            markers: grouped,
          }: {
            count: number;
            position: google.maps.LatLng;
            markers: google.maps.Marker[];
          }) {
            // Decide cluster colour based on contained markers
            const colorSet = new Set(
              grouped.map((gm: any) => gm.color || 'blue')
            );
            let clusterColor: string;
            if (colorSet.size === 1) {
              const only = [...colorSet][0];
              clusterColor = only === 'pink' ? '#FF1493' : only === 'orange' ? '#FF8C00' : '#007BFF';
            } else {
              clusterColor = '#800080'; // mixed = purple
            }
            const style = {
              url: clusterSVG(clusterColor),
              width: 90,
              height: 90,
              textColor: 'white',
              textSize: 14,
            };

            return new g.maps.Marker({
              position,
              icon: {
                url: style.url,
                scaledSize: new g.maps.Size(style.width, style.height),
                anchor: new g.maps.Point(style.width / 2, style.height / 2),
                labelOrigin: new g.maps.Point(
                  style.width / 2,
                  style.height / 2
                ),
              } as any,
              label: {
                text: String(count),
                color: style.textColor,
                fontSize: `${style.textSize}px`,
                fontWeight: '600',
              },
              zIndex: 1000 + count,
            });
          },
        } as any,
      });

      clustererRef.current = clusterer;

      // Initial application of disable states (if any were preset)
      if (disableBlue) setColorDisabled('blue', true);
      if (disablePink) setColorDisabled('pink', true);
      if (disableOrange) setColorDisabled('orange', true);
      applyHoverState();
    });

    return () => {
      cancelled = true;

      const clusterer = clustererRef.current;
      const map = mapRef.current;

      if (clusterer && (clusterer as any).setMap)
        (clusterer as any).setMap(null);
      // clear ALL markers (across buckets)
      Object.values(markersByColorRef.current).forEach((arr) => {
        arr.forEach((m) => m.setMap(null));
      });

      mapRef.current = null;
      mapsRef.current = null;
      clustererRef.current = null;
    };
    // Rebuild map if API key or locations change
  }, [apiKey, locations]);

  // === Legend wiring (DOM listeners on your provided section) ===
  useEffect(() => {
    if (!section) return;

    const blueEl = section.querySelector<HTMLElement>('[data-legend-blue]');
    const pinkEl = section.querySelector<HTMLElement>('[data-legend-pink]');
    const orangeEl = section.querySelector<HTMLElement>('[data-legend-orange]');

    // Hover handlers
    const onBlueEnter = () => setHoverOnBlue(true);
    const onBlueLeave = () => setHoverOnBlue(false);
    const onPinkEnter = () => setHoverOnPink(true);
    const onPinkLeave = () => setHoverOnPink(false);
    const onOrangeEnter = () => setHoverOnOrange(true);
    const onOrangeLeave = () => setHoverOnOrange(false);

    // Click/toggle handlers
    const onBlueClick = () => setDisableBlue((v) => !v);
    const onPinkClick = () => setDisablePink((v) => !v);
    const onOrangeClick = () => setDisableOrange((v) => !v);

    if (blueEl) {
      blueEl.addEventListener('mouseenter', onBlueEnter);
      blueEl.addEventListener('mouseleave', onBlueLeave);
      blueEl.addEventListener('click', onBlueClick);
      if (disableBlue) blueEl.setAttribute('disabled', '');
    }

    if (pinkEl) {
      pinkEl.addEventListener('mouseenter', onPinkEnter);
      pinkEl.addEventListener('mouseleave', onPinkLeave);
      pinkEl.addEventListener('click', onPinkClick);
      if (disablePink) pinkEl.setAttribute('disabled', '');
    }

    if (orangeEl) {
      orangeEl.addEventListener('mouseenter', onOrangeEnter);
      orangeEl.addEventListener('mouseleave', onOrangeLeave);
      orangeEl.addEventListener('click', onOrangeClick);
      if (disableOrange) orangeEl.setAttribute('disabled', '');
    }

    return () => {
      blueEl?.removeEventListener('mouseenter', onBlueEnter);
      blueEl?.removeEventListener('mouseleave', onBlueLeave);
      blueEl?.removeEventListener('click', onBlueClick);
      pinkEl?.removeEventListener('mouseenter', onPinkEnter);
      pinkEl?.removeEventListener('mouseleave', onPinkLeave);
      pinkEl?.removeEventListener('click', onPinkClick);
      orangeEl?.removeEventListener('mouseenter', onOrangeEnter);
      orangeEl?.removeEventListener('mouseleave', onOrangeLeave);
      orangeEl?.removeEventListener('click', onOrangeClick);
    };
  }, [section]); // attach once to provided section

  // Re-apply hover visuals whenever hover state changes
  useEffect(() => {
    applyHoverState();
  }, [hoverOnBlue, hoverOnPink, hoverOnOrange]);

  // When disable toggles flip, add/remove markers + update legend attributes
  useEffect(() => {
    const blueEl = section?.querySelector<HTMLElement>('[data-legend-blue]');
    if (blueEl) {
      if (disableBlue) blueEl.setAttribute('disabled', '');
      else blueEl.removeAttribute('disabled');
    }
    setColorDisabled('blue', disableBlue);
    // re-apply hover state after visibility changes (keeps visuals consistent)
    applyHoverState();
  }, [disableBlue, section]);

  useEffect(() => {
    const pinkEl = section?.querySelector<HTMLElement>('[data-legend-pink]');
    if (pinkEl) {
      if (disablePink) pinkEl.setAttribute('disabled', '');
      else pinkEl.removeAttribute('disabled');
    }
    setColorDisabled('pink', disablePink);
    applyHoverState();
  }, [disablePink, section]);

  useEffect(() => {
    const orangeEl = section?.querySelector<HTMLElement>('[data-legend-orange]');
    if (orangeEl) {
      if (disableOrange) orangeEl.setAttribute('disabled', '');
      else orangeEl.removeAttribute('disabled');
    }
    setColorDisabled('orange', disableOrange);
    applyHoverState();
  }, [disableOrange, section]);

  return (
    <div
      style={{ width: '100%', height: `${MAP_HEIGHT}px` }}
      ref={containerRef}
    />
  );
}
